/**
 * Page-level skeleton loaders for list views
 * Reusable loading states matching each page's layout
 */

import { Skeleton, SkeletonTableRow } from './Skeleton';

export function DashboardSkeleton() {
  return (
    <div className="space-y-8 pb-8 animate-in fade-in duration-300">
      {/* Header */}
      <div className="space-y-2">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-5 w-72" />
      </div>
      {/* Metric Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="claude-card p-6">
            <div className="flex items-start justify-between">
              <div className="space-y-3 flex-1">
                <Skeleton className="h-3 w-24" />
                <Skeleton className="h-10 w-20" />
                <Skeleton className="h-3 w-16" />
              </div>
              <Skeleton className="h-12 w-12" rounded="lg" />
            </div>
          </div>
        ))}
      </div>
      {/* Widget placeholders */}
      {Array.from({ length: 2 }).map((_, i) => (
        <div key={i} className="claude-card p-6 space-y-4">
          <Skeleton className="h-5 w-40" />
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, j) => (
              <Skeleton key={j} className="h-16 w-full" />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export function CustomersListSkeleton() {
  return (
    <div className="space-y-8 pb-8 animate-in fade-in duration-300">
      <div className="flex items-start justify-between">
        <div className="space-y-2">
          <Skeleton className="h-8 w-36" />
          <Skeleton className="h-5 w-64" />
        </div>
        <Skeleton className="h-10 w-36" rounded="lg" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="claude-card p-6">
            <div className="flex items-center gap-3">
              <Skeleton className="h-10 w-10" rounded="lg" />
              <div className="space-y-2 flex-1">
                <Skeleton className="h-3 w-24" />
                <Skeleton className="h-6 w-12" />
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="claude-card p-6">
        <Skeleton className="h-10 w-full" rounded="lg" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="claude-card p-6 space-y-4">
            <div className="flex items-start gap-4">
              <Skeleton className="h-14 w-14" rounded="lg" />
              <div className="space-y-2 flex-1">
                <Skeleton className="h-5 w-32" />
                <Skeleton className="h-3 w-24" />
              </div>
            </div>
            <div className="space-y-2">
              <Skeleton className="h-4 w-48" />
              <Skeleton className="h-4 w-32" />
            </div>
            <Skeleton className="h-px w-full" />
            <Skeleton className="h-9 w-full" rounded="lg" />
          </div>
        ))}
      </div>
    </div>
  );
}

export function TableListSkeleton({
  columns = 6,
  rows = 8,
  title = '',
}: {
  columns?: number;
  rows?: number;
  title?: string;
}) {
  return (
    <div className="space-y-8 pb-8 animate-in fade-in duration-300">
      <div className="flex items-start justify-between">
        <div className="space-y-2">
          <Skeleton className="h-8 w-36" />
          <Skeleton className="h-5 w-64" />
        </div>
        <Skeleton className="h-10 w-36" rounded="lg" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="claude-card p-6">
            <div className="flex items-start justify-between">
              <div className="space-y-3 flex-1">
                <Skeleton className="h-3 w-20" />
                <Skeleton className="h-10 w-24" />
                <Skeleton className="h-3 w-16" />
              </div>
              <Skeleton className="h-12 w-12" rounded="lg" />
            </div>
          </div>
        ))}
      </div>
      <div className="claude-card p-6">
        <Skeleton className="h-10 w-full" rounded="lg" />
      </div>
      <div className="claude-card overflow-hidden">
        <table className="claude-table" aria-label={`Loading ${title}`}>
          <thead>
            <tr>
              {Array.from({ length: columns }).map((_, i) => (
                <th key={i}>
                  <Skeleton className="h-4 w-20" />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: rows }).map((_, i) => (
              <SkeletonTableRow key={i} columns={columns} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function RecordingsGridSkeleton() {
  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      <div className="space-y-2">
        <Skeleton className="h-8 w-36" />
        <Skeleton className="h-5 w-64" />
      </div>
      <div className="claude-card p-4">
        <Skeleton className="h-10 w-full" rounded="lg" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="claude-card p-5 space-y-3">
            <Skeleton className="aspect-video w-full" rounded="lg" />
            <Skeleton className="h-5 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
            <div className="flex items-center gap-4">
              <Skeleton className="h-3 w-16" />
              <Skeleton className="h-3 w-16" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
